import type { ChangeEvent } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import type {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
} from "ag-grid-community";
import type { CustomFilterProps } from "ag-grid-react";
import { useGridFilter } from "ag-grid-react";
import { EventStatus } from "../../services/Types";
import { getStatusLabel } from "../../helpers/statusColors";

interface StatusFilterParams {
  viewPastEvents?: boolean;
  getDisplayStatus?: (data: { status?: string; updatedAt?: unknown }, viewPastEvents: boolean) => string;
}

export default (props: CustomFilterProps & StatusFilterParams) => {
  const { model, onModelChange, viewPastEvents, getDisplayStatus } = props;
  const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>();
  const [unappliedModel, setUnappliedModel] = useState(model);
  const appliedModelRef = useRef(model);

  appliedModelRef.current = model;

  const doesFilterPass = useCallback((params: IDoesFilterPassParams) => {
    const currentModel = appliedModelRef.current;
    if (currentModel == null) {
      return true;
    }
    const displayStatus = getDisplayStatus && viewPastEvents
      ? getDisplayStatus(params.data, viewPastEvents)
      : params.data.status;
    return displayStatus === currentModel;
  }, [viewPastEvents, getDisplayStatus]);

  const afterGuiAttached = useCallback(
    ({ hidePopup }: IAfterGuiAttachedParams) => {
      setCloseFilter(() => hidePopup);
    },
    [],
  );

  // register filter handlers with the grid
  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
  });

  useEffect(() => {
    setUnappliedModel(model);
  }, [model]);

  const onStatusChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    const newModel = value === "All" ? null : value;
    setUnappliedModel(newModel);
    appliedModelRef.current = newModel;
    onModelChange(newModel);
  };

  const onClick = () => {
    appliedModelRef.current = unappliedModel;
    onModelChange(unappliedModel);
    if (closeFilter) {
      closeFilter();
    }
  };

  return (
    <div className="status-filter">
      <label>
        <input
          key="All"
          type="radio"
          name="status"
          value="All"
          checked={unappliedModel == null}
          onChange={onStatusChange}
        />{" "}
        All
      </label>
      { Object.values(EventStatus).map((status) => (
        <label key={status}>
          <input
            type="radio"
            name="status"
            value={status}
            checked={unappliedModel === status}
            onChange={onStatusChange}
          />{" "}
          {getStatusLabel(status)}
        </label>
      ))}
      {viewPastEvents && (
        <label>
          <input
            type="radio"
            name="status"
            value="DORMANT"
            checked={unappliedModel === "DORMANT"}
            onChange={onStatusChange}
          />{" "}
          Dormant
        </label>
      )}
      <button onClick={onClick}>Apply</button>
    </div>
  );
};